import React, { useEffect, useCallback, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { injectIntl, useIntl } from "react-intl";
// Cloud React Components
import { Loading, Button } from "Components";
import VidFleetVideo from "Modules/.common/components/VidFleetVideo";
// Utils
import { GetFormatForMoment, checkDeviceData } from "Core/data/Helpers";
// Material UI
import { Grid, Typography } from "@mui/material";
import { getFormattedDateByTimeZone } from "Core/data/Helpers";
// Actions
import { getSnapshotRequest } from "../redux/slice";

const SnapshotError = ({ onRefresh }) => {
  const { messages } = useIntl();

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      spacing={1}
      style={{ padding: 30, height: 310 }}
    >
      <Grid item>
        <span style={{ fontSize: 14 }}>
          {messages["infoBubble_noSnapshotAvailable"]}
        </span>
      </Grid>

      <Grid item>
        <Button
          type="icon"
          icon="refresh"
          onClick={() => {
            onRefresh();
          }}
        />
      </Grid>
    </Grid>
  );
};

const Snapshot = (props) => {
  const dispatch = useDispatch();
  const { user, type, data, isEtaLink, ...rest } = props;
  const { messages, locale } = useIntl();
  const {
    loadingSnapshot: loading,
    snapshot: response,
    errorSnapshot: error,
  } = useSelector((state) => state.infoBubbleRedux);
  let deviceData = null;
  try {
    deviceData = checkDeviceData(data?.markerDetail?.vehicleTrail?.deviceData);
  } catch (error) {
    console.error("Error parsing device data", error);
  }

  const getSnapshot = useCallback(() => {
    // For LightMetrics ETA link, linkedDevice is null; fall back to unit ID
    const deviceId = data?.markerDetail?.linkedDevice?.id
      || (isEtaLink ? (data?.markerDetail?.unit?.linkedUnitId || data?.markerDetail?.unit?.id) : null);

    if (deviceId) {
      dispatch(
        getSnapshotRequest({
          deviceId: String(deviceId),
          unitTime: type === "Trail" ? data?.markerDetail?.vehicleTrail?.unitTime : new Date().toISOString(),
          isEtaLink: !!isEtaLink,
          companyId: data?.markerDetail?.unit?.companyId
        })
      );
    }
  }, [data]);

  const formatDateUser = useCallback(() => {
    let dateformat = user ? user?.dateformat : "m/d/Y";
    const momentFormat = GetFormatForMoment(dateformat);
    return `h:mm A, ${momentFormat}`;
  }, [user]);

  useEffect(() => {
    getSnapshot();
  }, [data]);

  if (loading) return isEtaLink ? <span /> : <Loading full />;

  if (error)
    return isEtaLink ? <span /> : (
      <SnapshotError
        onRefresh={() => {
          getSnapshot();
        }}
      />
    );

  if (!loading && !response?.url)
    return isEtaLink ? <span /> : (
      <SnapshotError
        onRefresh={() => {
          getSnapshot();
        }}
      />
    );

  if (type === "Trail" && deviceData?.url) {
    return (
      <div className="info-window-tab-video">
        <div>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item style={{ margin: "0 auto" }}>
              <div style={{ width: 350, height: 240 }}>
                <VidFleetVideo
                  messages={messages}
                  mode="picture"
                  pictureId={deviceData?.clipID}
                  deviceModel={
                    data?.markerDetail?.linkedDevice?.deviceModelName === "360"
                      ? "360"
                      : "4k"
                  }
                  thumbnailUrl={deviceData?.url || deviceData?.thumbnailUrl}
                />
              </div>

              <div style={{ padding: "8px 0px" }}>
                <Typography
                  noWrap
                  variant="subtitle2"
                  component="span"
                  style={{ fontWeight: "normal" }}
                >
                  {messages["infoBubble_snapshotDateTime"]}
                </Typography>
                <Typography
                  noWrap
                  variant="subtitle2"
                  component="span"
                  style={{ fontWeight: "bold" }}
                >
                  {getFormattedDateByTimeZone(
                    data?.markerDetail?.vehicleTrail?.unitTime,
                    data?.markerDetail?.unit?.timeZone?.name,
                    messages,
                    formatDateUser(),
                    true,
                    locale
                  )}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }

  return (
    <div className="info-window-tab-video">
      <div>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item style={{ margin: "0 auto" }}>
            <div style={{ width: rest?.width ?? 350, height: 240 }}>
              <VidFleetVideo
                messages={messages}
                mode="picture"
                pictureId={response?.pictureID}
                deviceModel={
                  data?.markerDetail?.linkedDevice?.deviceModelName === "360"
                    ? "360"
                    : "4k"
                }
                thumbnailUrl={response?.url}
              />
            </div>

            <div style={{ padding: "8px 0px" }}>
              <Typography
                noWrap
                variant="subtitle2"
                component="span"
                style={{ fontWeight: "normal" }}
              >
                {messages["infoBubble_snapshotDateTime"]}
              </Typography>
              <Typography
                noWrap
                variant="subtitle2"
                component="span"
                style={{ fontWeight: "bold" }}
              >
                {getFormattedDateByTimeZone(
                  response?.unitTime ||
                    data?.markerDetail?.vehicleTrail?.unitTime,
                  data?.markerDetail?.unit?.timeZone?.name,
                  messages,
                  formatDateUser(),
                  true,
                  locale
                )}
              </Typography>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default injectIntl(memo(Snapshot));
